import React from "react";
import Link from "next/link";
import Head from "next/head";
import { fetchArticles } from "../../../utils/article";
import { useRouter } from "next/router";
import { 
  Alert,
  Avatar,
  Chip,
  Typography 
} from "@material-tailwind/react";
import RightSidenav from "../../../components/waydev/right-side-menu";
import { SoftwareNavbar } from "../../../components/navbar";
import Footer from "../../../components/footer";

const ROOT_URL= "software-engineering-intelligence"
const ROOT_ARTICLES = "articles/waydev"
const PARTNER = "waydev"

const fetchRecentPosts = (articles: any[], numPosts = 5) => {
  const sortedArticles = articles.sort(
    (a, b) =>
      new Date(b.frontMatter.date).getTime() -
      new Date(a.frontMatter.date).getTime(),
  );

  return sortedArticles.slice(0, numPosts);
};

interface TagPageProps {
  articles: Article[];
  tag: string;
  recentPosts: Article[];
  topTags: any[];
  title: string;
  description: string;
}

export interface Article {
  slug: string;
  frontMatter: {
    articleThumbnail: string;
    title: string;
    description: string;
    tag: string[];
    authorName: string;
    authorAvatar: string;
    tags: string[];
  };
}

const getTopTags = (articles: any) => {
  const tagCounts = {};

  articles.forEach((article: any) => {
    const tags = article.frontMatter.tag;

    if (tags) {
      tags.forEach((tag) => {
        if (tagCounts[tag]) {
          tagCounts[tag]++;
        } else {
          tagCounts[tag] = 1;
        }
      });
    }
  });

  const topTags = Object.keys(tagCounts).sort(
    (a, b) => tagCounts[b] - tagCounts[a]
  ).slice(0, 5);

  return topTags.map((tag) => ({
    tag,
    count: tagCounts[tag],
  }));
};

export async function getStaticProps({ params }) {
  const tag = params.tag as string;
  const allArticles = fetchArticles(ROOT_ARTICLES);
  const recentPosts = fetchRecentPosts(allArticles);
  const topTags = getTopTags(allArticles);


  // Filter articles that contain the specified tag
  const filteredArticles = allArticles.filter((article: any) =>
    article.frontMatter.tag.map((t) => t.toLowerCase().replace(/\s/g, '-')).includes(tag),
  );

  return {
    props: {
      articles: filteredArticles,
      tag,
      recentPosts,
      topTags,
      title: tag == 'saas' ? 'SaaS' : tag == 'startup' ? 'Startups' : tag == 'enterprise' ? 'Enterprises' : 'Software',
      description: tag == 'saas' ? 'Check out the latest news for tech enthusiasts related to SaaS. Explore what’s new in the industry!' : tag == 'startup' ? 'Check out the latest news for tech enthusiasts related to Startups. See what’s new in this ever-changing industry!' : tag == 'enterprise' ? 'Check out the latest news for tech enthusiasts related to Enterprises. Discover what is new in the industry and stay-up-to-date!' : 'Check out the latest news regarding SaaS and software for tech enthusiasts. Check out what’s new in the industry!',
    },
  };
}




const TagPage: React.FC<TagPageProps> = ({ articles, recentPosts, tag, topTags, title, description }) => {
  const router = useRouter();

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Head>
        <title>Lastest News in the Software World to {title} </title>
        <meta
          name="description"
          content={`${description}`}
        />
        <link
          rel="canonical"
          href={`https://www.creative-tim.com/${ROOT_URL}/tag/${tag}`}
        />
        <meta property="og:title" content={`Lastest News in the Software World to ${title}`} />
        <meta property="og:url" content={`https://www.creative-tim.com/${ROOT_URL}/tag/${tag}` } />
        <meta property="og:description" content={`${description}`} />
      </Head>
      
      <div className="relative mb-8 h-full w-full bg-white">
        <SoftwareNavbar partner={`${PARTNER}`} />
        <div className="px-8 lg:px-52">
          <div className="container mx-auto lg:flex">
            <div className="mt-6 w-full lg:w-[65%] lg:px-8">
              <Typography variant="h1" className="mb-8">
                Latest News Related to {tag
                  .split('-')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')}
              </Typography>
              {/* <Typography variant="paragraph" className="mb-8">
                We cover the latest news in the Tailwind CSS world related to {tag}. Check out what’s new in the industry!
              </Typography> */}
              {articles.map((article) => (
                <div key={article.slug} className="mb-12">
                  <a href={`/${ROOT_URL}/${article.slug}`}>
                    {article.frontMatter.articleThumbnail && (
                      <img
                        src={article.frontMatter.articleThumbnail}
                        alt={article.frontMatter.title}
                        className="rounded-lg shadow-xl mb-8"
                      />
                    )}
                    <Typography variant="h4">{article.frontMatter.title}</Typography>
                  </a>
                  <Typography variant="paragraph" className="mb-4">
                    {article.frontMatter.description}
                    <a
                      className="ml-2 font-normal text-blue-600"
                      href={`/${ROOT_URL}/${article.slug}`}
                    >
                      Read More &rarr;
                    </a>
                  </Typography>

                  <div className="flex items-center border-b pb-6">
                    <Avatar
                      size="sm"
                      src={article.frontMatter.authorAvatar || "https://raw.githubusercontent.com/creativetimofficial/public-assets/master/blog/default-avatar.png"}
                      alt={article.frontMatter.authorName}
                      className="shadow"
                    />
                    <div>
                      <Typography variant="h6" className="ml-2">
                        {article.frontMatter.authorName}
                      </Typography>
                      {article.frontMatter.tags &&
                        Array.isArray(article.frontMatter.tags) && (
                          <Typography
                            variant="small"
                            color="gray"
                            className="font-normal"
                          >
                            {article.frontMatter.tags.join(", ")}
                          </Typography>
                        )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="w-full lg:w-80 ml-auto">
              <RightSidenav recentPosts={recentPosts} topTags={topTags}  partner={`${PARTNER}`} />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export async function getStaticPaths() {
  const allArticles = fetchArticles(ROOT_ARTICLES);
  const tagsSet = new Set<string>();

  allArticles.forEach((article: any) => {
    const tags = article.frontMatter.tag;
    if (tags) {
      tags.forEach((tag) => tagsSet.add(tag));
    }
  });

  const paths = Array.from(tagsSet).map((tag) => ({
    params: { 
      tag: tag.toLowerCase().replace(/\s/g, '-')
    },
  }));

  return { paths, fallback: false };
}

export default TagPage;
